import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import {
    CheckCircle, ArrowLeft, Zap, Shield, Clock, Users,
    BarChart3, Globe, Code2, Server, Smartphone, Database
} from 'lucide-react';
import Navbar from '../Navbar/Navbar';
import Footer from '../Footer/Footer';

const servicesDetailData = {
    'web-development': {
        title: 'Web Development',
        subheading: 'Creative & SEO-Friendly Solutions',
        description: 'Transform your digital presence with cutting-edge web solutions that combine stunning aesthetics with powerful functionality. Our expert developers craft responsive, high-performance websites that drive results.',
        imagePath: '/assest/services/WebDevelopment.png',
        longDescription: `We specialize in creating modern, responsive websites that not only look stunning but also perform exceptionally. Our development process focuses on user experience, search engine optimization, and conversion optimization.

From simple landing pages to complex web applications, we handle projects of all sizes with the same dedication to quality and attention to detail.`,
        features: [
            { icon: <Globe className="w-6 h-6" />, title: 'Responsive Design', desc: 'Perfect viewing on all devices from mobile to desktop' },
            { icon: <Zap className="w-6 h-6" />, title: 'SEO Optimization', desc: 'Built-in best practices for search engine visibility' },
            { icon: <Code2 className="w-6 h-6" />, title: 'CMS Integration', desc: 'Easy content management with WordPress, Strapi, or custom solutions' },
            { icon: <BarChart3 className="w-6 h-6" />, title: 'E-Commerce Ready', desc: 'Full-featured online stores with payment integration' },
        ],
        technologies: ['React', 'Next.js', 'Node.js', 'Tailwind CSS', 'MongoDB', 'PostgreSQL'],
        process: [
            { step: '01', title: 'Discovery', desc: 'Understanding your goals and requirements' },
            { step: '02', title: 'Design', desc: 'Creating wireframes and visual designs' },
            { step: '03', title: 'Development', desc: 'Building with clean, scalable code' },
            { step: '04', title: 'Launch', desc: 'Deployment and performance optimization' },
        ],
        stats: [
            { value: '150+', label: 'Websites Delivered' },
            { value: '98%', label: 'Client Satisfaction' },
            { value: '100%', label: 'Responsive Design' },
        ]
    },
    'web-hosting': {
        title: 'Web Hosting',
        subheading: 'Reliable & Scalable Infrastructure',
        description: 'Enterprise-grade hosting solutions that keep your website running 24/7. From shared hosting to dedicated servers, we provide the perfect infrastructure for your needs.',
        imagePath: '/assest/services/WebHosting.png',
        longDescription: `Our hosting infrastructure is built for speed, security, and reliability. With data centers across the globe, we ensure your website loads fast for visitors everywhere.

We offer flexible hosting plans that grow with your business, from startup-friendly shared hosting to powerful dedicated servers.`,
        features: [
            { icon: <Server className="w-6 h-6" />, title: '99.9% Uptime', desc: 'Guaranteed availability with SLA-backed promises' },
            { icon: <Shield className="w-6 h-6" />, title: 'Free SSL Certificate', desc: 'Automatic HTTPS encryption for all domains' },
            { icon: <Clock className="w-6 h-6" />, title: '24/7 Support', desc: 'Round-the-clock technical assistance' },
            { icon: <Database className="w-6 h-6" />, title: 'Daily Backups', desc: 'Automated daily backups with easy restoration' },
        ],
        technologies: ['cPanel', 'CloudLinux', 'LiteSpeed', 'NVMe SSD', 'Cloudflare', 'Lets', 'Encrypt'],
        process: [
            { step: '01', title: 'Choose Plan', desc: 'Select the perfect hosting package' },
            { step: '02', title: 'Setup', desc: 'Instant account activation and configuration' },
            { step: '03', title: 'Migrate', desc: 'Free website migration assistance' },
            { step: '04', title: 'Monitor', desc: 'Continuous performance monitoring' },
        ],
        stats: [
            { value: '99.9%', label: 'Uptime Guarantee' },
            { value: '50+', label: 'Free Apps' },
            { value: '24/7', label: 'Expert Support' },
        ]
    },
    'software-development': {
        title: 'Software Development',
        subheading: 'Custom Business Solutions',
        description: 'Tailored software solutions that streamline your business operations. We build robust applications using modern technologies that scale with your growth.',
        imagePath: '/assest/services/SoftwareDev.png',
        longDescription: `Our software development team creates custom solutions that address your unique business challenges. From desktop applications to enterprise systems, we deliver software that works exactly how you need it.

We follow agile methodologies to ensure timely delivery and continuous improvement throughout the development lifecycle.`,
        features: [
            { icon: <Code2 className="w-6 h-6" />, title: '.NET Framework', desc: 'Enterprise-grade Windows applications' },
            { icon: <Database className="w-6 h-6" />, title: 'Database Integration', desc: 'Seamless data management and reporting' },
            { icon: <Globe className="w-6 h-6" />, title: 'Cloud Ready', desc: 'Deploy on AWS, Azure, or private cloud' },
            { icon: <Zap className="w-6 h-6" />, title: 'API Development', desc: 'RESTful and GraphQL API solutions' },
        ],
        technologies: ['.NET Core', 'C#', 'SQL Server', 'Azure', 'Docker', 'Kubernetes'],
        process: [
            { step: '01', title: 'Analysis', desc: 'Deep dive into business requirements' },
            { step: '02', title: 'Architecture', desc: 'System design and technology selection' },
            { step: '03', title: 'Build', desc: 'Iterative development with regular demos' },
            { step: '04', title: 'Deploy', desc: 'Production deployment and training' },
        ],
        stats: [
            { value: '200+', label: 'Projects Completed' },
            { value: '50+', label: 'Enterprise Clients' },
            { value: '100%', label: 'Custom Solutions' },
        ]
    },
    'erp-systems': {
        title: 'ERP Systems',
        subheading: 'Streamline Your Operations',
        description: 'Integrated business management systems that connect all departments. Real-time data, automated workflows, and intelligent reporting for smarter decisions.',
        imagePath: '/assest/services/ERPSystems.png',
        longDescription: `Our ERP solutions unify your business processes into a single, powerful platform. From inventory to finance, HR to sales, everything connects seamlessly.

We customize ERP implementations to match your exact workflow, ensuring minimal disruption and maximum efficiency gains.`,
        features: [
            { icon: <BarChart3 className="w-6 h-6" />, title: 'Inventory Management', desc: 'Real-time stock tracking and optimization' },
            { icon: <Database className="w-6 h-6" />, title: 'Financial Modules', desc: 'Complete accounting and financial reporting' },
            { icon: <Users className="w-6 h-6" />, title: 'HR & Payroll', desc: 'Employee management and automated payroll' },
            { icon: <Zap className="w-6 h-6" />, title: 'Business Intelligence', desc: 'AI-powered insights and analytics' },
        ],
        technologies: ['Odoo', 'SAP', 'Oracle', 'Power BI', 'Python', 'PostgreSQL'],
        process: [
            { step: '01', title: 'Audit', desc: 'Current system analysis and gap identification' },
            { step: '02', title: 'Plan', desc: 'Module selection and customization roadmap' },
            { step: '03', title: 'Implement', desc: 'Phased rollout with data migration' },
            { step: '04', title: 'Optimize', desc: 'Continuous improvement and support' },
        ],
        stats: [
            { value: '40%', label: 'Efficiency Gain' },
            { value: '100%', label: 'Data Accuracy' },
            { value: '30%', label: 'Cost Reduction' },
        ]
    },
    'financial-systems': {
        title: 'Financial Systems',
        subheading: 'Secure & Compliant Accounting',
        description: 'Enterprise-grade financial solutions with intelligent reporting. Compliant, secure, and designed for complex accounting needs across multiple jurisdictions.',
        imagePath: '/assest/services/Financial.png',
        longDescription: `Our financial systems provide comprehensive accounting capabilities with built-in compliance features. Handle multi-currency transactions, complex tax scenarios, and detailed audit trails with confidence.

Designed for businesses that demand accuracy, security, and real-time financial visibility.`,
        features: [
            { icon: <Shield className="w-6 h-6" />, title: 'Tax Management', desc: 'Automated tax calculation and filing' },
            { icon: <Globe className="w-6 h-6" />, title: 'Multi-Currency', desc: 'Support for 100+ currencies with live rates' },
            { icon: <CheckCircle className="w-6 h-6" />, title: 'Audit Trails', desc: 'Complete transaction history and logs' },
            { icon: <BarChart3 className="w-6 h-6" />, title: 'Real-time Reports', desc: 'Instant financial dashboards and reports' },
        ],
        technologies: ['QuickBooks', 'Xero', 'Sage', 'Stripe', 'Plaid', 'Tableau'],
        process: [
            { step: '01', title: 'Assess', desc: 'Financial workflow and compliance review' },
            { step: '02', title: 'Configure', desc: 'Chart of accounts and tax rules setup' },
            { step: '03', title: 'Integrate', desc: 'Bank and payment gateway connections' },
            { step: '04', title: 'Report', desc: 'Custom reporting and dashboard creation' },
        ],
        stats: [
            { value: '100%', label: 'Compliance' },
            { value: '50+', label: 'Tax Rules' },
            { value: 'Real-time', label: 'Reporting' },
        ]
    },
    'mobile-app-development': {
        title: 'Mobile App Development',
        subheading: 'iOS, Android & Cross-Platform',
        description: 'Native and cross-platform mobile applications that engage users. From concept to app store, we handle the complete development cycle with precision.',
        imagePath: '/assest/services/MobileApps.png',
        longDescription: `We create mobile experiences that users love. Whether you need a native iOS app, Android solution, or cross-platform application, our team delivers polished, performant apps.

Our process covers everything from UX research to app store optimization, ensuring your app stands out in crowded marketplaces.`,
        features: [
            { icon: <Smartphone className="w-6 h-6" />, title: 'Native Performance', desc: 'Platform-optimized for speed and smoothness' },
            { icon: <Zap className="w-6 h-6" />, title: 'Push Notifications', desc: 'Engage users with targeted messaging' },
            { icon: <Database className="w-6 h-6" />, title: 'Offline Mode', desc: 'Full functionality without internet' },
            { icon: <Globe className="w-6 h-6" />, title: 'App Store Publishing', desc: 'End-to-end store submission support' },
        ],
        technologies: ['React Native', 'Flutter', 'Swift', 'Kotlin', 'Firebase', 'AWS Amplify'],
        process: [
            { step: '01', title: 'Ideate', desc: 'Concept validation and market research' },
            { step: '02', title: 'Design', desc: 'UI/UX design and prototyping' },
            { step: '03', title: 'Develop', desc: 'Agile development with bi-weekly builds' },
            { step: '04', title: 'Launch', desc: 'Store submission and marketing assets' },
        ],
        stats: [
            { value: '100+', label: 'Apps Published' },
            { value: '4.8★', label: 'Average Rating' },
            { value: '1M+', label: 'Downloads' },
        ]
    }
};

const ServiceDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const service = servicesDetailData[slug];

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    if (!service) {
        return (
            <div className="min-h-screen bg-[#0a1628] flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-4xl font-bold text-white mb-4">Service Not Found</h1>
                    <button onClick={() => navigate('/services')} className="text-[#EFCD86] hover:underline">
                        Back to Services
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0a1628] relative selection:bg-[#EFCD86]/30">
            {/* Background */}
            <div className="fixed inset-0 z-0 bg-[url('/assest/logo.jfif')] bg-cover bg-center opacity-20 grayscale pointer-events-none" style={{ backgroundAttachment: 'fixed' }} />
            <div className="fixed inset-0 z-0 bg-gradient-to-b from-[#0a1628]/95 via-[#0a1628]/85 to-[#0a1628]/95 pointer-events-none" />

            <Navbar />

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 z-10">
                <div className="max-w-7xl mx-auto px-4">
                    {/* Back Button */}
                    <motion.button
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        onClick={() => navigate('/services')}
                        className="group flex items-center gap-2 text-gray-400 hover:text-[#EFCD86] transition-colors mb-12"
                    >
                        <ArrowLeft className="w-5 h-5 transform group-hover:-translate-x-1 transition-transform" />
                        <span className="font-medium">Back to Services</span>
                    </motion.button>

                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        {/* Text Content */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                        >
                            <span className="inline-block py-1 px-3 rounded-full bg-[#EFCD86]/10 border border-[#EFCD86]/20 text-[#EFCD86] text-xs font-semibold tracking-widest uppercase mb-6">
                                Service Details
                            </span>
                            <h1 className="text-5xl md:text-7xl font-bold text-white mb-4 tracking-tight">
                                {service.title}
                            </h1>
                            <h2 className="text-2xl md:text-3xl font-medium text-[#EFCD86] mb-6">
                                {service.subheading}
                            </h2>
                            <p className="text-xl text-gray-300 leading-relaxed mb-8">
                                {service.description}
                            </p>

                            {/* Stats */}
                            <div className="grid grid-cols-3 gap-6">
                                {service.stats.map((stat, i) => (
                                    <motion.div
                                        key={i}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.2 + i * 0.1 }}
                                        className="text-center"
                                    >
                                        <div className="text-3xl md:text-4xl font-bold text-[#EFCD86] mb-1">{stat.value}</div>
                                        <div className="text-sm text-gray-400">{stat.label}</div>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>

                        {/* Image */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.8 }}
                            className="relative"
                        >
                            <div className="absolute inset-0 bg-gradient-to-r from-[#EFCD86]/20 to-[#046A8C]/20 rounded-3xl blur-3xl" />
                            <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
                                <img src={service.imagePath} alt={service.title} className="w-full h-[400px] object-cover" />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628]/60 to-transparent" />
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Long Description */}
            <section className="relative z-10 py-16">
                <div className="max-w-4xl mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7 }}
                        className="bg-[#0d1f33]/50 border border-white/10 rounded-2xl p-8 md:p-12 backdrop-blur-sm"
                    >
                        <p className="text-lg text-gray-300 leading-relaxed whitespace-pre-line">
                            {service.longDescription}
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Features Grid */}
            <section className="relative z-10 py-20">
                <div className="max-w-7xl mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Key Features</h2>
                        <p className="text-gray-400 text-lg">Everything you need to succeed</p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {service.features.map((feature, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1, duration: 0.6 }}
                                className="group bg-[#0d1f33] border border-white/10 rounded-2xl p-8 hover:border-[#EFCD86]/30 transition-all duration-500 hover:shadow-[0_0_30px_rgba(239,205,134,0.1)]"
                            >
                                <div className="w-14 h-14 rounded-xl bg-[#EFCD86]/10 border border-[#EFCD86]/20 flex items-center justify-center text-[#EFCD86] mb-6 group-hover:scale-110 transition-transform duration-300">
                                    {feature.icon}
                                </div>
                                <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                                <p className="text-gray-400 leading-relaxed">{feature.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Technologies */}
            <section className="relative z-10 py-20">
                <div className="max-w-7xl mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Technologies</h2>
                        <p className="text-gray-400 text-lg">Tools we use to build excellence</p>
                    </motion.div>

                    <div className="flex flex-wrap justify-center gap-4">
                        {service.technologies.map((tech, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, scale: 0.8 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.05, duration: 0.4 }}
                                className="px-6 py-3 bg-[#0d1f33] border border-white/10 rounded-full text-gray-300 font-medium hover:border-[#EFCD86]/30 hover:text-[#EFCD86] transition-all duration-300 cursor-default"
                            >
                                {tech}
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Process Steps */}
            <section className="relative z-10 py-20 pb-32">
                <div className="max-w-7xl mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Our Process</h2>
                        <p className="text-gray-400 text-lg">How we deliver excellence</p>
                    </motion.div>

                    <div className="grid md:grid-cols-4 gap-8">
                        {service.process.map((item, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.15, duration: 0.6 }}
                                className="relative"
                            >
                                {/* Connector line */}
                                {i < service.process.length - 1 && (
                                    <div className="hidden md:block absolute top-12 left-[60%] w-[80%] h-px bg-gradient-to-r from-[#EFCD86]/50 to-transparent" />
                                )}

                                <div className="text-6xl font-black text-[#EFCD86]/20 mb-4">{item.step}</div>
                                <h3 className="text-2xl font-bold text-white mb-3">{item.title}</h3>
                                <p className="text-gray-400 leading-relaxed">{item.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="relative z-10 py-20">
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="bg-gradient-to-r from-[#EFCD86]/10 via-[#046A8C]/10 to-[#EFCD86]/10 border border-[#EFCD86]/20 rounded-3xl p-12 backdrop-blur-sm"
                    >
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready to Get Started?</h2>
                        <p className="text-gray-400 text-lg mb-8">Let's discuss how we can help transform your business</p>
                        <button className="group inline-flex items-center gap-3 bg-[#EFCD86] text-[#0a1628] px-8 py-4 rounded-full text-lg font-bold hover:bg-white transition-all duration-300 shadow-[0_0_30px_rgba(239,205,134,0.3)]">
                            <span>Start Your Project</span>
                            <ArrowLeft className="w-5 h-5 rotate-180 transform group-hover:translate-x-1 transition-transform" />
                        </button>
                    </motion.div>
                </div>
            </section>

            <div className="relative z-10">
                <Footer />
            </div>

            <style>{`@keyframes float { 0%,100%{transform:translate(0,0)} 50%{transform:translate(15px,-20px)} }`}</style>
        </div>
    );
};

export default ServiceDetail;